import * as React from 'react';
import { Box, GlobalStyles } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { SyncActiveAppFromPath } from '../apps/SyncActiveAppFromPath';
import AppBar from './AppBar';
import { LicenseGraceBanner } from './LicenseGraceBanner';
import { LicensePreExpiryBanner } from './LicensePreExpiryBanner';

const ACCESS_DENIED_TOAST_ID = 'fbr-access-denied';

// Codes that indicate the entire app/resource is blocked — navigate away instead of showing a toast.
const APP_LEVEL_CODES = new Set(['active_app_denied', 'active_app_required']);

function AccessDeniedNotifier() {
    const navigate = useNavigate();

    React.useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<{ code?: string; message?: string }>).detail ?? {};
            const code = detail.code ?? '';
            const msg =
                detail.message?.trim() ||
                'Access denied — you do not have permission for this action.';

            if (APP_LEVEL_CODES.has(code)) {
                // Navigate to the select-app page so the user can choose an app they do have access to,
                // or sees the "no apps assigned" message with clear guidance.
                navigate('/select-app', { replace: false });
                return;
            }

            toast.error(msg, {
                toastId: ACCESS_DENIED_TOAST_ID,
                containerId: ACCESS_DENIED_TOAST_ID,
                autoClose: 5000,
            });
        };
        window.addEventListener('fbr-access-denied', handler);
        return () => window.removeEventListener('fbr-access-denied', handler);
    }, [navigate]);
    return null;
}

export function AppShell(props: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                minHeight: '100dvh',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                // Keep the app constrained to viewport; scrolling happens in the content area.
                overflow: 'hidden',
            }}
        >
            <GlobalStyles
                styles={theme => ({
                    /**
                     * Autocomplete options are rendered in a portal (Popper), so component-level `sx`
                     * doesn't reliably style the "Create ..." suggestion. Make it global + theme-based.
                     */
                    '.RaAutocompleteInput-createSuggestion, .RaAutocompleteInput-createSuggestion *': {
                        color: `${(theme.vars || theme).palette.secondary.main} !important`,
                        fontWeight: '700 !important',
                    },
                    '.RaAutocompleteInput-createSuggestion': {
                        borderTop: '1px solid rgba(0,0,0,0.08)',
                    },
                })}
            />
            <ToastContainer
                containerId={ACCESS_DENIED_TOAST_ID}
                position="top-right"
                style={{ top: 70 }}
                newestOnTop
                closeOnClick
                pauseOnHover
                limit={3}
                theme="light"
            />
            <AccessDeniedNotifier />
            <AppBar />
            <LicensePreExpiryBanner />
            <LicenseGraceBanner />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    // Hairline gap under the top bar (ERP-style tight header).
                    pt: '2px',
                }}
            >
                <SyncActiveAppFromPath />
                {props.children}
            </Box>
        </Box>
    );
}

export default AppShell;
